import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFinalCalculations, useMidWorkDayCutoff } from '@/api/hooks/useFinalCalculations'
import { useWorkOrder } from '@/api/hooks/useWorkOrder'
import PageLayout from '@/components/layout/page-layout'
import CarSummary from '@/components/molecules/car-summary'
import { EmptyState } from '@/components/molecules/empty-state'
import CabasDateCard from '@/components/organisms/slutkalkyl-details/cabas-date-card'
import LeadTimesCard from '@/components/organisms/slutkalkyl-details/lead-times-card'
import SlutkalkylStatus from '@/components/organisms/slutkalkyl-details/slutkalkyl-status'
import WorkOrderTimeline from '@/components/organisms/slutkalkyl-details/work-order-timeline'
import {
  DATE_FIELDS,
  dateSyncEntries,
} from '@/components/organisms/slutkalkyl-table/slutkalkyl.utils'
import {
  WorkOrderModal,
  WorkOrderModalTab,
} from '@/components/organisms/work-order-modal/work-order-modal'
import { Dialog } from '@/components/ui/dialog'
import { useSetSidebarSubLabel } from '@/contexts/sidebar-sub-label-context'
import { useAppSelector } from '@/redux/hooks'

export const Route = createFileRoute('/_authenticated/slutkalkyler/$workOrderId')({
  component: SlutkalkylRoute,
})

function SlutkalkylRoute() {
  const { workOrderId } = Route.useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data: workOrder } = useWorkOrder(workOrderId)
  const registrationNumber = workOrder?.car?.registrationNumber

  // Shares the list page's cache, so coming from the table needs no extra request.
  const activeWorkshopId = useAppSelector(state => state.settings.activeWorkshopId)
  const { data: finalCalculations, isLoading } = useFinalCalculations(activeWorkshopId ?? '')
  const cutoff = useMidWorkDayCutoff()
  const finalCalculation = finalCalculations.find(({ id }) => id === workOrderId)

  useSetSidebarSubLabel(`/slutkalkyler/${workOrderId}`, registrationNumber)

  // The modal reads its active tab from the hash, so set it before opening.
  const openModal = () => {
    navigate({ hash: WorkOrderModalTab.Planning, replace: true, search: true })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    // The modal keeps its active tab in the hash; don't leave it behind on this page.
    navigate({ hash: '', replace: true, search: true })
  }

  return (
    <PageLayout title={t('slutkalkylPage.title')} subtitle={registrationNumber}>
      <div className="flex flex-col gap-6">
        {registrationNumber && (
          <CarSummary
            registrationNumber={registrationNumber}
            make={workOrder?.car?.make}
            model={workOrder?.car?.model}
            states={finalCalculation?.currentStates ?? []}
            renderPlate={plate => (
              <button
                type="button"
                className="w-fit shrink-0 cursor-pointer"
                aria-label={t('slutkalkylPage.openWorkOrder')}
                onClick={openModal}
              >
                {plate}
              </button>
            )}
          />
        )}

        {finalCalculation ? (
          <>
            <SlutkalkylStatus workOrder={finalCalculation} />

            <section className="flex flex-col gap-3">
              <h2 className="heading-3">{t('slutkalkylPage.cabasDatesHeading')}</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {DATE_FIELDS.map(field => (
                  <CabasDateCard
                    key={field.id}
                    label={t(`slutkalkylTable.${field.id}Column`)}
                    entries={dateSyncEntries(finalCalculation, field, cutoff)}
                  />
                ))}
              </div>
            </section>

            <div className="grid items-start gap-4 lg:grid-cols-2">
              <WorkOrderTimeline workOrder={finalCalculation} />
              <LeadTimesCard workOrder={finalCalculation} />
            </div>
          </>
        ) : (
          !isLoading && (
            <EmptyState
              heading={t('slutkalkylPage.notInHandoffHeading')}
              body={t('slutkalkylPage.notInHandoffBody')}
            />
          )
        )}
      </div>

      {isModalOpen && (
        <Dialog open onOpenChange={open => !open && closeModal()}>
          <WorkOrderModal workOrderId={workOrderId} />
        </Dialog>
      )}
    </PageLayout>
  )
}
